import { useEffect, useRef, useState } from 'react';

export interface MenuOption {
  value: string;
  label: string;
}

/**
 * ehr#478 (PROGRESS_NOTE_10-14-V81 mountNoteMenu, Chase 10-17): the button beside the note's title
 * that changes the template, then the layout. A custom list on desktop, a native grouped select on
 * touch. The layouts are the progress note's own (Default layout and Crisis); the V81 "Manage
 * layouts" editor (a practice's own layouts) isn't built.
 */
export function NoteTemplateMenu({
  format,
  formats,
  layout,
  layouts,
  disabled,
  onFormat,
  onLayout,
}: {
  format: string;
  formats: MenuOption[];
  /** The layout in use (its value in `layouts`). */
  layout: string;
  /** None: the note has no layouts to switch between. */
  layouts: MenuOption[];
  disabled?: boolean;
  onFormat: (v: string) => void;
  onLayout: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const coarse =
    typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', close, true);
    window.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('mousedown', close, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [open]);
  const label = formats.find((f) => f.value === format)?.label ?? format;
  const pick = (v: string) => {
    setOpen(false);
    if (v.startsWith('t:')) onFormat(v.slice(2));
    else if (v.startsWith('l:')) onLayout(v.slice(2));
  };
  const groups: [string, string, MenuOption[], string][] = [
    ['Template', 't:', formats, format],
    ...(layouts.length
      ? [['Layout', 'l:', layouts, layout] as [string, string, MenuOption[], string]]
      : []),
  ];
  if (coarse)
    return (
      <select
        className="pn-tm-native"
        aria-label="Template and layout"
        value={`t:${format}`}
        disabled={disabled}
        onChange={(e) => pick(e.target.value)}
      >
        {groups.map(([g, p, opts]) => (
          <optgroup key={g} label={g}>
            {opts.map((o) => (
              <option key={o.value} value={`${p}${o.value}`}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    );
  return (
    <div className={`pn-tm${open ? ' open' : ''}`} ref={ref}>
      <button
        type="button"
        className="pn-tm-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Template and layout: ${label}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{label}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="pn-tm-pop" role="listbox" aria-label="Template and layout">
          {groups.map(([g, p, opts, cur]) => (
            <div key={g} role="group" aria-label={g}>
              <div className="pn-tm-g">{g}</div>
              {opts.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === cur}
                  className={`pn-tm-opt${o.value === cur ? ' on' : ''}`}
                  onClick={() => pick(`${p}${o.value}`)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
